import { useTranslation } from "react-i18next";
import type { LiveStreamDisplayStatus } from "@/types";

const STATUS_STYLES: Record<
  LiveStreamDisplayStatus,
  { className: string; dotClassName?: string }
> = {
  planned: { className: "bg-yellow-100 text-yellow-800 border border-yellow-200" },
  starting: {
    className: "bg-blue-100 text-blue-800 border border-blue-200",
    dotClassName: "bg-blue-500 animate-pulse",
  },
  live: {
    className: "bg-green-100 text-green-800 border border-green-200",
    dotClassName: "bg-green-500 animate-pulse",
  },
  stopping: {
    className: "bg-orange-100 text-orange-800 border border-orange-200",
    dotClassName: "bg-orange-500 animate-pulse",
  },
  offline: {
    className: "bg-gray-100 text-gray-700 border border-gray-200",
    dotClassName: "bg-gray-400",
  },
  failed: {
    className: "bg-red-100 text-red-800 border border-red-200",
    dotClassName: "bg-red-500",
  },
  ended: { className: "bg-red-100 text-red-800 border border-red-200" },
  deleted: { className: "bg-gray-100 text-gray-400 border border-gray-200" },
  unknown: {
    className: "bg-gray-100 text-gray-500 border border-gray-200",
    dotClassName: "bg-gray-400",
  },
};

/**
 * Badge for the server-derived live-stream display status, which reflects
 * device-reported evidence rather than the raw user-intent state.
 */
export function LiveStreamStatusBadge({
  status,
}: {
  status: LiveStreamDisplayStatus;
}) {
  const { t } = useTranslation("pages");
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.className}`}
    >
      {style.dotClassName && (
        <span
          className={`w-1.5 h-1.5 rounded-full inline-block ${style.dotClassName}`}
        />
      )}
      {t(`sessions.detail.liveStream.status.${status}`)}
    </span>
  );
}
